import type { MeshBuilder, Frame, VertexStyle } from './meshBuilder';
import type { Vec2 } from '../../geo/types';
import type { DetailSink } from '../fabric/detailSink';
import type { TracerySink } from '../fabric/landmarks/tracery/tracerySink';
import type { ChunkSink } from './chunker';

/** Place local building geometry on a world datum without changing its dimensions.
 * Painter exaggeration bases move with the building, rather than stretching its foundation. */
export function elevatedBuilder(builder: MeshBuilder, height: number): MeshBuilder {
  if (height === 0) return builder;
  const frameAt = (frame: Frame): Frame => ({
    ...frame,
    origin: [frame.origin[0], frame.origin[1] + height, frame.origin[2]],
  });
  const styleAt = (style: VertexStyle): VertexStyle =>
    style.exaggeration
      ? {
          ...style,
          exaggeration: { ...style.exaggeration, base: style.exaggeration.base + height },
        }
      : style;
  return {
    quad: (style, frame, corners, uv) => {
      builder.quad(styleAt(style), frameAt(frame), corners, uv);
    },
    polygon: (style, frame, outline, uv) => {
      builder.polygon(styleAt(style), frameAt(frame), outline, uv);
    },
    box: (style, frame, min, max, faces) => {
      builder.box(styleAt(style), frameAt(frame), min, max, faces);
    },
    prism: (style, frame, base, y0, y1, faces) => {
      builder.prism(styleAt(style), frameAt(frame), base, y0, y1, faces);
    },
    cylinder: (style, frame, base, r0, r1, h, segments, faces) => {
      builder.cylinder(styleAt(style), frameAt(frame), base, r0, r1, h, segments, faces);
    },
    groundPolygon: (style, polygon, y, config, uv) => {
      builder.groundPolygon(
        styleAt(style),
        polygon,
        typeof y === 'number' ? y + height : (p) => y(p) + height,
        config,
        uv
      );
    },
    get vertexCount() {
      return builder.vertexCount;
    },
    get triangleCount() {
      return builder.triangleCount;
    },
    finish: () => builder.finish(),
    build: () => builder.build(),
  };
}

export function elevatedChunks(
  chunks: ChunkSink,
  elevation: number | ((p: Vec2) => number)
): ChunkSink {
  return {
    builderAt(layer, point) {
      return elevatedBuilder(
        chunks.builderAt(layer, point),
        typeof elevation === 'number' ? elevation : elevation(point)
      );
    },
  };
}

export function elevatedDetails(sink: DetailSink, height: number): DetailSink {
  return {
    ...(sink.roof
      ? ({
          roof(profile) {
            const raise = (p: typeof profile.from): typeof profile.from => [
              p[0],
              p[1] + height,
              p[2],
            ];
            sink.roof!({
              from: raise(profile.from),
              to: raise(profile.to),
              chimneys: profile.chimneys.map(raise),
            });
          },
        } satisfies Pick<DetailSink, 'roof'>)
      : {}),
    add(kind, pose, edgeId) {
      sink.add(
        kind,
        { ...pose, origin: [pose.origin[0], pose.origin[1] + height, pose.origin[2]] },
        edgeId
      );
    },
  };
}

export function elevatedTracery(sink: TracerySink, height: number): TracerySink {
  return {
    add(window, edgeId) {
      sink.add(
        { ...window, origin: [window.origin[0], window.origin[1] + height, window.origin[2]] },
        edgeId
      );
    },
  };
}
